import VideoThumbnailRow from '../../components/VideoThumbnailRow';
import VideoThumbnailService from '../../services/VideoThumbnailService';
import { thumbnailPayload } from '../../components/thumbnails/thumbnailFiles';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Autocomplete,
  InputAdornment,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  MoreVert as MoreVertIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { surface } from '../../theme';
import SleepVideoV3Service from '../../services/SleepVideoV3Service';
import ChannelService from '../../services/ChannelService';
import VoiceOverConfigService from '../../services/VoiceOverConfigService';
import AuthService from '../../services/AuthService';
import { pickRandomChannelColor } from '../../utils/channelColors';
import ChannelBadge from '../../components/ChannelBadge';
import { SCRIPT_STATUS_CHIP, POLL_INTERVAL, pageSize } from './constants';
import { copyToClipboard } from './utils';
import PipelineStatusCell from './PipelineStatusCell';
import CreateVideoDialog from './CreateVideoDialog';
import AddChannelDialog from './AddChannelDialog';
import EditVideoDialog from './EditVideoDialog';
import DeleteVideoDialog from './DeleteVideoDialog';
import QrCodeManagerDialog from './QrCodeManagerDialog';
import MobileListCard from '../../components/responsive/MobileListCard';
import { triggerBrowserDownload, videoDownloadFilename } from '../../utils/download';

export default function SleepVideosV3Page() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [filterChannel, setFilterChannel] = useState(null);
  const [filterChannels, setFilterChannels] = useState([]);
  const [filterChannelsLoading, setFilterChannelsLoading] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchDebounceRef = useRef(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [createThumbnail, setCreateThumbnail] = useState(undefined);
  const [editThumbnail, setEditThumbnail] = useState(undefined);
  const [thumbnailBusy, setThumbnailBusy] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createVideoContext, setCreateVideoContext] = useState('');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedVoiceOverConfig, setSelectedVoiceOverConfig] = useState(null);
  const [voiceOverConfigs, setVoiceOverConfigs] = useState([]);
  const [voiceOverConfigsLoading, setVoiceOverConfigsLoading] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [qrCodesLoading, setQrCodesLoading] = useState(false);
  const [qrCodeManagerOpen, setQrCodeManagerOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelProfileNumber, setNewChannelProfileNumber] = useState('');
  const [addingChannel, setAddingChannel] = useState(false);
  const [addChannelError, setAddChannelError] = useState(null);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [editItem, setEditItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editVideoContext, setEditVideoContext] = useState('');
  const [editChannel, setEditChannel] = useState(null);
  const [editQrCode, setEditQrCode] = useState(null);
  const [editChannels, setEditChannels] = useState([]);
  const [editChannelsLoading, setEditChannelsLoading] = useState(false);
  const [editLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [copiedTitleId, setCopiedTitleId] = useState(null);
  const [togglingUsedId, setTogglingUsedId] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuItem, setMobileMenuItem] = useState(null);
  const pollRef = useRef(null);

  const loadItems = useCallback(async (p, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await SleepVideoV3Service.list({ page: p + 1, ...params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterChannel?.id]);

  useEffect(() => {
    const params = {};
    if (filterChannel?.id) params.channelId = filterChannel.id;
    if (searchDebounced?.trim()) params.search = searchDebounced.trim();
    loadItems(page, params);
  }, [page, filterChannel?.id, searchDebounced, loadItems]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const next = searchTitle?.trim() ?? '';
      setSearchDebounced(next);
      setPage(0);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTitle]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, filterChannel?.id, searchDebounced]);

  const hasActiveVoiceOver = (i) =>
    i.voiceOverGenerationStatus?.status === 'queued' ||
    i.voiceOverGenerationStatus?.status === 'generating-voiceover' ||
    i.voiceOverGenerationStatus?.status === 'generating';

  useEffect(() => {
    const hasActiveScript = items.some(
      (i) =>
        i.generationStatus?.status === 'generating' ||
        i.generationStatus?.status === 'queued' ||
        i.generationStatus?.status === 'rate limit',
    );
    const hasActiveRender = items.some((i) =>
      ['queued', 'setting_up', 'rendering', 'finalizing'].includes(i.renderStatus?.status),
    );
    const hasActiveVoiceOverPoll = items.some(hasActiveVoiceOver);
    if (!hasActiveScript && !hasActiveRender && !hasActiveVoiceOverPoll) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const [scriptStatuses, renderStatusesData, voiceOverStatusesData] = await Promise.all([
          SleepVideoV3Service.listScriptStatuses(),
          SleepVideoV3Service.listRenderStatuses(),
          SleepVideoV3Service.listVoiceOverStatuses(),
        ]);
        const scriptMap = new Map(scriptStatuses.map((s) => [s.id, s]));
        setItems((prev) =>
          prev.map((item) => {
            const scriptUpdate = scriptMap.get(item.id);
            const renderUpdate = renderStatusesData[item.id];
            const voiceOverUpdate = voiceOverStatusesData[item.id];
            return {
              ...item,
              ...(scriptUpdate && {
                generationStatus: {
                  ...item.generationStatus,
                  status: scriptUpdate.status,
                  progress: scriptUpdate.progress,
                  stepLabel: scriptUpdate.stepLabel,
                },
              }),
              ...(renderUpdate && { renderStatus: renderUpdate }),
              ...(voiceOverUpdate && { voiceOverGenerationStatus: voiceOverUpdate }),
            };
          }),
        );
      } catch (_) {}
    }, POLL_INTERVAL);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [items]);

  const loadChannels = useCallback(async (search = '') => {
    try {
      setChannelsLoading(true);
      const list = await ChannelService.list(search);
      setChannels(Array.isArray(list) ? list : []);
    } catch {
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  const loadVoiceOverConfigs = useCallback(async () => {
    try {
      setVoiceOverConfigsLoading(true);
      const list = await VoiceOverConfigService.list();
      setVoiceOverConfigs(Array.isArray(list) ? list : []);
    } catch {
      setVoiceOverConfigs([]);
    } finally {
      setVoiceOverConfigsLoading(false);
    }
  }, []);

  const loadQrCodes = useCallback(async () => {
    try {
      setQrCodesLoading(true);
      const data = await SleepVideoV3Service.listQrCodes();
      const list = Array.isArray(data.items) ? data.items : [];
      setQrCodes(list);
      return list;
    } catch {
      setQrCodes([]);
      return [];
    } finally {
      setQrCodesLoading(false);
    }
  }, []);

  const handleOpenDialog = () => {
    setCreateThumbnail(undefined);
    setThumbnailBusy(false);
    setCreateTitle('');
    setCreateVideoContext('');
    setSelectedChannel(null);
    setSelectedVoiceOverConfig(null);
    setSelectedQrCode(null);
    setCreateError(null);
    setDialogOpen(true);
    loadChannels('');
    loadVoiceOverConfigs();
    loadQrCodes();
  };

  const handleCloseDialog = () => {
    if (!creating && !thumbnailBusy) {
      setDialogOpen(false);
      setCreateError(null);
    }
  };

  const handleCreate = async () => {
    if (creating || thumbnailBusy) return;
    const title = createTitle?.trim();
    if (!title) {
      setCreateError('Title is required');
      return;
    }
    if (!selectedChannel?.id) {
      setCreateError('Channel is required');
      return;
    }
    if (!selectedChannel?.footageFolderId) {
      setCreateError('Configure a footage folder for this channel before creating a video.');
      return;
    }
    if (!selectedVoiceOverConfig?.id && !selectedChannel?.voiceOverConfigId) {
      setCreateError('Voice Over config is required');
      return;
    }
    try {
      setCreating(true);
      setCreateError(null);
      const created = await SleepVideoV3Service.create({
        title,
        videoTitle: title,
        thumbnail: await thumbnailPayload(createThumbnail),
        videoContext: createVideoContext?.trim() || '',
        channelId: selectedChannel.id,
        voiceOverConfigId: selectedVoiceOverConfig?.id,
        qrCodeId: selectedQrCode?.id,
      });
      setDialogOpen(false);
      navigate(`/sleep-videos/${created.id}`);
    } catch (err) {
      setCreateError(err.response?.data?.error || err.message || 'Failed to create video');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenAddChannel = () => {
    setNewChannelName('');
    setNewChannelProfileNumber('');
    setAddChannelError(null);
    setAddChannelOpen(true);
  };

  const handleCloseAddChannel = () => {
    if (!addingChannel) {
      setAddChannelOpen(false);
      setNewChannelName('');
      setNewChannelProfileNumber('');
      setAddChannelError(null);
    }
  };

  const handleAddChannel = async () => {
    const name = newChannelName?.trim();
    if (!name) {
      setAddChannelError('Channel name is required');
      return;
    }
    const num = parseInt(newChannelProfileNumber, 10);
    if (Number.isNaN(num) || num < 1) {
      setAddChannelError('Profile number must be an integer greater than 0');
      return;
    }
    try {
      setAddingChannel(true);
      setAddChannelError(null);
      const created = await ChannelService.create({
        name,
        profileNumber: num,
        color: pickRandomChannelColor(),
      });
      setChannels((prev) => [...prev, created]);
      setSelectedChannel(created);
      setAddChannelOpen(false);
      setNewChannelName('');
      setNewChannelProfileNumber('');
    } catch (err) {
      setAddChannelError(err.response?.data?.error || err.message || 'Failed to create channel');
    } finally {
      setAddingChannel(false);
    }
  };

  const handleDeleteClick = (item, e) => {
    e.stopPropagation();
    if (selectedIds.size > 0 && selectedIds.has(item.id)) {
      const payloadItems = items
        .filter((i) => selectedIds.has(i.id))
        .map((i) => ({ id: i.id, title: i.title || '' }));
      setDeleteItem({ items: payloadItems });
    } else {
      setDeleteItem({ id: item.id, title: item.title || '' });
    }
  };
  const openMobileMenu = (event, item) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuItem(item);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuItem(null);
  };

  const handleDeleteClose = () => {
    if (!deleting) setDeleteItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    try {
      setDeleting(true);
      if (deleteItem.items) {
        const results = await Promise.allSettled(
          deleteItem.items.map((it) => SleepVideoV3Service.delete(it.id)),
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          const first = failed[0].reason;
          const msg = first?.response?.data?.error || first?.message || 'Failed to delete';
          setError(
            failed.length === deleteItem.items.length
              ? msg
              : `${deleteItem.items.length - failed.length} deleted; ${failed.length} failed: ${msg}`,
          );
        }
      } else {
        await SleepVideoV3Service.delete(deleteItem.id);
      }
      setDeleteItem(null);
      setSelectedIds(new Set());
      const params = {};
      if (filterChannel?.id) params.channelId = filterChannel.id;
      if (searchDebounced?.trim()) params.search = searchDebounced.trim();
      loadItems(page, params);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEdit = (item, e) => {
    e.stopPropagation();
    setEditItem(item);
    setEditThumbnail(undefined);
    setThumbnailBusy(false);
    setEditTitle(item.title);
    setEditVideoContext('');
    setEditChannel(
      item.channel
        ? {
            id: item.channel.id,
            name: item.channel.name,
            color: item.channel.color ?? '',
            profileNumber: item.channel.profileNumber,
          }
        : null,
    );
    setEditQrCode(null);
    setEditError(null);
    setEditChannelsLoading(true);
    ChannelService.list('')
      .then((list) => setEditChannels(Array.isArray(list) ? list : []))
      .finally(() => setEditChannelsLoading(false));
    Promise.all([SleepVideoV3Service.get(item.id), loadQrCodes()])
      .then(([data, list]) => {
        setEditVideoContext(data.videoContext ?? '');
        const selected =
          data.qrCode ||
          (Array.isArray(list) ? list.find((qr) => qr.id === data.qrCodeId) : null) ||
          null;
        setEditQrCode(selected);
      })
      .catch(() => {});
  };

  const handleCloseEdit = () => {
    if (!savingEdit && !editLoading && !thumbnailBusy) {
      setEditItem(null);
      setEditError(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editItem || savingEdit || thumbnailBusy) return;
    const title = editTitle?.trim();
    if (!title) {
      setEditError('Title is required');
      return;
    }
    if (!editChannel?.id) {
      setEditError('Channel is required');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      await SleepVideoV3Service.update(editItem.id, {
        title,
        videoTitle: title,
        thumbnail: await thumbnailPayload(editThumbnail),
        videoContext: editVideoContext?.trim() || '',
        channelId: editChannel.id,
        qrCodeId: editQrCode?.id || null,
      });
      VideoThumbnailService.notifyChanged('sleep', editItem.id);
      setEditItem(null);
      const params = {};
      if (filterChannel?.id) params.channelId = filterChannel.id;
      if (searchDebounced?.trim()) params.search = searchDebounced.trim();
      loadItems(page, params);
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  return (
    <Box>
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        Sleep Videos
      </Typography>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 2, width: { xs: '100%', sm: 'auto' } }}
            >
              New Video
            </Button>
            <Button
              variant='outlined'
              startIcon={<DescriptionIcon />}
              onClick={() => navigate('/sleep-videos/prompt-system/edit')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
            >
              Edit Prompt System
            </Button>
            <Button
              variant='outlined'
              startIcon={<RecordVoiceOverIcon />}
              onClick={() => navigate('/sleep-videos/voice-over-configs')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
            >
              Voice Over
            </Button>
            <Button
              variant='outlined'
              startIcon={<QrCodeIcon />}
              onClick={() => setQrCodeManagerOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
            >
              QR Codes
            </Button>
            <Autocomplete
              sx={{ minWidth: 0, width: { xs: '100%', sm: 320 } }}
              options={filterChannels}
              getOptionLabel={(opt) =>
                (opt && (opt.profileNumber != null ? `${opt.name} (#${opt.profileNumber})` : opt.name)) || ''
              }
              value={filterChannel}
              onChange={(_, val) => setFilterChannel(val)}
              loading={filterChannelsLoading}
              filterOptions={(x) => x}
              onOpen={() => {
                setFilterChannelsLoading(true);
                ChannelService.list('')
                  .then((list) => setFilterChannels(Array.isArray(list) ? list : []))
                  .finally(() => setFilterChannelsLoading(false));
              }}
              onInputChange={(_, value) => {
                setFilterChannelsLoading(true);
                ChannelService.list(value || '')
                  .then((list) => setFilterChannels(Array.isArray(list) ? list : []))
                  .finally(() => setFilterChannelsLoading(false));
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ChannelBadge
                    channel={{
                      ...option,
                      name:
                        option.profileNumber != null
                          ? `${option.name} (#${option.profileNumber})`
                          : option.name,
                    }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} size='small' label='Filter by channel' placeholder='All channels' />
              )}
            />
            <TextField
              size='small'
              placeholder='Search by title'
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 0, width: { xs: '100%', sm: 220 } }}
            />
          </Box>

          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography color='text.secondary' sx={{ py: 3 }}>
              No videos yet. Click "New Video" to create one.
            </Typography>
          ) : isMobile ? (
            <>
              {items.map((item) => {
                const scriptStatus = item.generationStatus?.status || 'idle';
                const scriptChip = SCRIPT_STATUS_CHIP[scriptStatus] || SCRIPT_STATUS_CHIP.idle;
                return (
                  <VideoThumbnailRow
                    component={MobileListCard}
                    kind='sleep' videoId={item.id} videoTitle={item.title}
                    key={item.id}
                    title={item.title}
                    subtitle={item.channel?.name || 'No channel'}
                    meta={item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}
                    onClick={() => navigate(`/sleep-videos/${item.id}`)}
                    primaryAction={
                      <Button size='small' onClick={() => navigate(`/sleep-videos/${item.id}`)} sx={{ minWidth: 0, px: 1 }}>
                        Open
                      </Button>
                    }
                    secondaryAction={
                      <IconButton size='small' onClick={(e) => openMobileMenu(e, item)}>
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    }
                    statusSummary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip label={scriptChip.label || scriptStatus} size='small' color={scriptChip.color || 'default'} />
                        <Chip
                          label={
                            item.renderStatus?.status === 'completed'
                              ? 'video ready'
                              : item.generationStatus?.status === 'completed'
                                ? 'script ready'
                                : 'processing'
                          }
                          size='small'
                          variant='outlined'
                        />
                      </Box>
                    }
                  />
                );
              })}
              <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={closeMobileMenu}>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuItem) navigate(`/sleep-videos/${mobileMenuItem.id}`);
                    closeMobileMenu();
                  }}
                >
                  Open details
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!mobileMenuItem) return;
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(mobileMenuItem.id)) next.delete(mobileMenuItem.id);
                      else next.add(mobileMenuItem.id);
                      return next;
                    });
                    closeMobileMenu();
                  }}
                >
                  Toggle selection
                </MenuItem>
                <MenuItem
                  disabled={!mobileMenuItem || togglingUsedId === mobileMenuItem.id}
                  onClick={async () => {
                    if (!mobileMenuItem) return;
                    closeMobileMenu();
                    setTogglingUsedId(mobileMenuItem.id);
                    try {
                      await SleepVideoV3Service.update(mobileMenuItem.id, { used: !(mobileMenuItem.used ?? false) });
                      setItems((prev) =>
                        prev.map((it) => (it.id === mobileMenuItem.id ? { ...it, used: !(it.used ?? false) } : it)),
                      );
                    } finally {
                      setTogglingUsedId(null);
                    }
                  }}
                >
                  {(mobileMenuItem?.used ?? false) ? 'Mark as unused' : 'Mark as used'}
                </MenuItem>
                <MenuItem
                  disabled={!mobileMenuItem || mobileMenuItem.generationStatus?.status !== 'completed'}
                  onClick={async () => {
                    if (!mobileMenuItem || mobileMenuItem.generationStatus?.status !== 'completed') return;
                    closeMobileMenu();
                    try {
                      const data = await SleepVideoV3Service.get(mobileMenuItem.id);
                      if (data?.fullScript) await copyToClipboard(data.fullScript);
                    } catch {
                      // ignore
                    }
                  }}
                >
                  Copy script
                </MenuItem>
                <MenuItem
                  disabled={!mobileMenuItem || mobileMenuItem.renderStatus?.status !== 'completed'}
                  onClick={async () => {
                    if (!mobileMenuItem || mobileMenuItem.renderStatus?.status !== 'completed') return;
                    closeMobileMenu();
                    try {
                      const data = await SleepVideoV3Service.get(mobileMenuItem.id);
                      if (data?.videoUrl) {
                        triggerBrowserDownload(
                          data.videoUrl,
                          videoDownloadFilename(
                            data.channel?.name,
                            data.videoTitle || data.title,
                            '.mp4',
                            'sleep-video',
                          ),
                        );
                      }
                    } catch {
                      // ignore
                    }
                  }}
                >
                  Download video
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    if (mobileMenuItem) handleOpenEdit(mobileMenuItem, e);
                    closeMobileMenu();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={(e) => {
                    if (mobileMenuItem) handleDeleteClick(mobileMenuItem, e);
                    closeMobileMenu();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Pagination
                  count={Math.max(1, Math.ceil(total / pageSize))}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                />
              </Box>
            </>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox' sx={{ fontWeight: 700 }}>
                        <Checkbox
                          size='small'
                          indeterminate={
                            items.some((i) => selectedIds.has(i.id)) &&
                            !items.every((i) => selectedIds.has(i.id))
                          }
                          checked={
                            items.length > 0 && items.every((i) => selectedIds.has(i.id))
                          }
                          onChange={() => {
                            const ids = items.map((i) => i.id);
                            setSelectedIds((prev) => {
                              const allSelected =
                                ids.length > 0 && ids.every((id) => prev.has(id));
                              const next = new Set(prev);
                              if (allSelected) {
                                ids.forEach((id) => next.delete(id));
                              } else {
                                ids.forEach((id) => next.add(id));
                              }
                              return next;
                            });
                          }}
                          inputProps={{ 'aria-label': 'Select all on this page' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 88 }}>Thumbnail</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 180, maxWidth: 280 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700, maxWidth: 140 }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => {
                      const scriptStatus = item.generationStatus?.status || 'idle';
                      const scriptChip = SCRIPT_STATUS_CHIP[scriptStatus] || SCRIPT_STATUS_CHIP.idle;
                      const progress = item.generationStatus?.progress ?? 0;
                      return (
                        <VideoThumbnailRow
                          kind='sleep' videoId={item.id} videoTitle={item.title}
                          key={item.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            ...((item.used ?? false) && {
                              opacity: 0.6,
                              bgcolor: 'grey.300',
                              '& .MuiTableCell-root': {
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                              },
                            }),
                          }}
                          onClick={() => navigate(`/sleep-videos/${item.id}`)}
                        >
                          <TableCell
                            padding='checkbox'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              size='small'
                              checked={selectedIds.has(item.id)}
                              onChange={() => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(item.id)) next.delete(item.id);
                                  else next.add(item.id);
                                  return next;
                                });
                              }}
                              inputProps={{ 'aria-label': `Select ${item.title || 'video'}` }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                component='span'
                                variant='body2'
                                title={item.title || ''}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Tooltip title={copiedTitleId === item.id ? 'Copied!' : 'Copy title'}>
                                <IconButton
                                  size='small'
                                  aria-label='Copy title'
                                  sx={{ flexShrink: 0, textDecoration: 'none' }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const ok = await copyToClipboard(item.title || '');
                                    if (ok) {
                                      setCopiedTitleId(item.id);
                                      setTimeout(() => setCopiedTitleId(null), 2000);
                                    }
                                  }}
                                >
                                  {copiedTitleId === item.id ? (
                                    <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                  ) : (
                                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 140 }}>
                            {item.channel ? (
                              <ChannelBadge
                                channel={{
                                  ...item.channel,
                                  name:
                                    item.channel.profileNumber != null
                                      ? `${item.channel.name} (#${item.channel.profileNumber})`
                                      : item.channel.name,
                                }}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <PipelineStatusCell
                              item={item}
                              scriptStatus={scriptStatus}
                              scriptChip={scriptChip}
                              progress={progress}
                              now={Date.now()}
                            />
                          </TableCell>
                          <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                            {selectedIds.size > 0 && selectedIds.has(item.id) ? (
                              <Tooltip
                                title={
                                  selectedIds.size > 1
                                    ? `Delete ${selectedIds.size} selected videos`
                                    : 'Delete'
                                }
                              >
                                <IconButton
                                  size='small'
                                  color='error'
                                  onClick={(e) => handleDeleteClick(item, e)}
                                  title='Delete'
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title={(item.used ?? false) ? 'Mark as unused' : 'Mark as used'}>
                                  <span>
                                    <IconButton
                                      size='small'
                                      disabled={togglingUsedId === item.id}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setTogglingUsedId(item.id);
                                        try {
                                          await SleepVideoV3Service.update(item.id, {
                                            used: !(item.used ?? false),
                                          });
                                          setItems((prev) =>
                                            prev.map((it) =>
                                              it.id === item.id ? { ...it, used: !(it.used ?? false) } : it,
                                            ),
                                          );
                                        } catch {
                                          // ignore
                                        } finally {
                                          setTogglingUsedId(null);
                                        }
                                      }}
                                      color={(item.used ?? false) ? 'success' : 'default'}
                                      title={(item.used ?? false) ? 'Used' : 'Unused'}
                                    >
                                      {(item.used ?? false) ? (
                                        <CheckCircleIcon fontSize='small' />
                                      ) : (
                                        <CheckCircleOutlineIcon fontSize='small' />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    item.generationStatus?.status === 'completed'
                                      ? 'Copy script'
                                      : 'Script not completed yet'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size='small'
                                      disabled={item.generationStatus?.status !== 'completed'}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (item.generationStatus?.status !== 'completed') return;
                                        try {
                                          const data = await SleepVideoV3Service.get(item.id);
                                          if (data?.fullScript) await copyToClipboard(data.fullScript);
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                      title='Copy script'
                                    >
                                      <DescriptionIcon fontSize='small' />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    item.renderStatus?.status === 'completed'
                                      ? 'Download video'
                                      : 'Video not rendered yet'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size='small'
                                      disabled={item.renderStatus?.status !== 'completed'}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (item.renderStatus?.status !== 'completed') return;
                                        try {
                                          const data = await SleepVideoV3Service.get(item.id);
                                          if (data?.videoUrl) {
                                            triggerBrowserDownload(
                                              data.videoUrl,
                                              videoDownloadFilename(
                                                data.channel?.name,
                                                data.videoTitle || data.title,
                                                '.mp4',
                                                'sleep-video',
                                              ),
                                            );
                                          }
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                      title='Download video'
                                    >
                                      <DownloadIcon fontSize='small' />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <IconButton size='small' onClick={(e) => handleOpenEdit(item, e)} title='Edit'>
                                  <EditIcon fontSize='small' />
                                </IconButton>
                                <IconButton size='small' onClick={(e) => handleDeleteClick(item, e)} title='Delete'>
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </VideoThumbnailRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
                </Typography>
                <Pagination
                  count={Math.max(1, Math.ceil(total / pageSize))}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                  siblingCount={5}
                  boundaryCount={1}
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <QrCodeManagerDialog
        open={qrCodeManagerOpen}
        onClose={() => setQrCodeManagerOpen(false)}
        onChanged={(nextItems) => {
          setQrCodes(Array.isArray(nextItems) ? nextItems : []);
          setSelectedQrCode((current) =>
            current && nextItems?.some((item) => item.id === current.id)
              ? nextItems.find((item) => item.id === current.id)
              : null,
          );
          setEditQrCode((current) =>
            current && nextItems?.some((item) => item.id === current.id)
              ? nextItems.find((item) => item.id === current.id)
              : null,
          );
        }}
      />

      <CreateVideoDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        creating={creating}
        thumbnail={createThumbnail}
        setThumbnail={setCreateThumbnail}
        thumbnailBusy={thumbnailBusy}
        setThumbnailBusy={setThumbnailBusy}
        createError={createError}
        onDismissError={() => setCreateError(null)}
        createTitle={createTitle}
        setCreateTitle={setCreateTitle}
        createVideoContext={createVideoContext}
        setCreateVideoContext={setCreateVideoContext}
        selectedVoiceOverConfig={selectedVoiceOverConfig}
        setSelectedVoiceOverConfig={setSelectedVoiceOverConfig}
        voiceOverConfigs={voiceOverConfigs}
        voiceOverConfigsLoading={voiceOverConfigsLoading}
        loadVoiceOverConfigs={loadVoiceOverConfigs}
        selectedQrCode={selectedQrCode}
        setSelectedQrCode={setSelectedQrCode}
        qrCodes={qrCodes}
        qrCodesLoading={qrCodesLoading}
        loadQrCodes={loadQrCodes}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
        channels={channels}
        channelsLoading={channelsLoading}
        loadChannels={loadChannels}
        handleCreate={handleCreate}
        onOpenAddChannel={handleOpenAddChannel}
      />

      <AddChannelDialog
        open={addChannelOpen}
        onClose={handleCloseAddChannel}
        addingChannel={addingChannel}
        addChannelError={addChannelError}
        newChannelName={newChannelName}
        setNewChannelName={setNewChannelName}
        newChannelProfileNumber={newChannelProfileNumber}
        setNewChannelProfileNumber={setNewChannelProfileNumber}
        handleAddChannel={handleAddChannel}
      />

      <EditVideoDialog
        open={!!editItem}
        onClose={handleCloseEdit}
        editItem={editItem}
        editTitle={editTitle}
        setEditTitle={setEditTitle}
        editVideoContext={editVideoContext}
        setEditVideoContext={setEditVideoContext}
        editChannel={editChannel}
        setEditChannel={setEditChannel}
        editChannels={editChannels}
        editChannelsLoading={editChannelsLoading}
        editQrCode={editQrCode}
        setEditQrCode={setEditQrCode}
        qrCodes={qrCodes}
        qrCodesLoading={qrCodesLoading}
        loadQrCodes={loadQrCodes}
        editError={editError}
        setEditError={setEditError}
        handleSaveEdit={handleSaveEdit}
        savingEdit={savingEdit}
        thumbnail={editThumbnail}
        setThumbnail={setEditThumbnail}
        thumbnailBusy={thumbnailBusy}
        setThumbnailBusy={setThumbnailBusy}
      />

      <DeleteVideoDialog
        deleteItem={deleteItem}
        deleting={deleting}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
